/* ============================================================
   Login.jsx — entry login, split panel.
   Credentials are checked by the API; nothing is validated here.
   ============================================================ */

import { useEffect, useState } from 'react';
import { LOGO } from '../components/ui.jsx';
import { auth } from '../store/store.js';
import { api } from '../store/api.js';
import { APP_VERSION } from '../version.js';

/* The kiosk logs in as a team, admin as a named user. */
const ROLES = {
  kiosk: { label: 'Kiosk', nameLabel: 'Lag (veckans ansvar)', namePlaceholder: 't.ex. Lag P12', passLabel: 'Kiosk-kod' },
  admin: { label: 'Admin', nameLabel: 'Användarnamn', namePlaceholder: '', passLabel: 'Lösenord' },
};

/* A successful login updates the shared session, which re-renders App into
   the right view — nothing to hand back to the caller. */
export function Login() {
  const [role, setRole] = useState('kiosk');
  const [name, setName] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState(null);   // null = inte hämtade än

  /* Kiosken loggar in som ett lag — hämta rullistan innan inloggning. */
  useEffect(() => {
    api.get('/teams')
      .then(d => setTeams(Array.isArray(d.teams) ? d.teams : []))
      .catch(() => setTeams([]));
  }, []);

  const cfg = ROLES[role];
  /* Alltid en rullista för kiosken — avstängd med "Hämtar lag…" tills listan
     kommit, så fältet inte byter skepnad mitt framför ögonen. Fritext bara
     om det faktiskt inte finns några lag inlagda. */
  const teamsLoading = teams === null;
  const teamPicker = role === 'kiosk' && (teamsLoading || teams.length > 0);
  /* Admin kontrolleras på både användarnamn och lösenord. */
  const canSubmit = !busy && !!pass && !(teamPicker && !name) && !(role === 'admin' && !name.trim());

  function chooseRole(r) {
    setRole(r);
    setErr('');
    setName('');
    setPass('');
  }

  async function doLogin() {
    if (!canSubmit) return;
    setBusy(true);
    setErr('');
    try {
      await auth.login(role, pass, name);
    } catch (e) {
      setErr(e.message || 'Inloggningen misslyckades.');
    } finally {
      setBusy(false);
    }
  }

  const onKey = e => { if (e.key === 'Enter') doLogin(); };

  const form = (
    <>
      <div style={S.seg}>
        {Object.entries(ROLES).map(([id, r]) => (
          <button key={id} onClick={() => chooseRole(id)}
            style={{ ...S.segBtn, ...(role === id ? S.segOn : {}) }}>{r.label}</button>
        ))}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={S.lbl}>{cfg.nameLabel}</label>
        {teamPicker ? (
          <select className="select" value={name} disabled={teamsLoading} style={teamsLoading ? { opacity: .7 } : {}}
            onChange={e => { setName(e.target.value); setErr(''); }}>
            {teamsLoading
              ? <option value="">Hämtar lag…</option>
              : <>
                <option value="" disabled>Välj ert lag…</option>
                {teams.map(t => <option key={t} value={t}>{t}</option>)}
              </>}
          </select>
        ) : (
          <input value={name} autoComplete="username" autoCapitalize="none" spellCheck={false} onChange={e => { setName(e.target.value); setErr(''); }} onKeyDown={onKey}
            placeholder={cfg.namePlaceholder} style={S.inp} />
        )}
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={S.lbl}>{cfg.passLabel}</label>
        <input type="password" value={pass} onChange={e => { setPass(e.target.value); setErr(''); }} onKeyDown={onKey}
          placeholder="••••••" style={S.inp} autoComplete="current-password" />
      </div>
      {err && <div style={S.err}>{err}</div>}
      <button onClick={doLogin} disabled={!canSubmit}
        style={{ ...S.submit, ...(!canSubmit ? S.submitOff : {}) }}>
        {busy ? 'Loggar in…' : 'Logga in'}
      </button>
    </>
  );

  return (
    <div className="stage login-split">
      <div className="login-hero">
        {/* The club emblem is already a round badge with its own ring, so it
            stands on the green by itself — no white disc behind it. */}
        <img src={LOGO} alt="Sandareds IF" style={{
          width: 124, height: 124, objectFit: 'contain', flexShrink: 0,
          filter: 'drop-shadow(0 8px 24px rgba(0,0,0,.3))',
        }} />
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 40, fontWeight: 800, lineHeight: 1.08, margin: '28px 0 14px' }}>
          Sandareds IF<br />Kiosken
        </h1>
        <p style={{ fontSize: 16, color: 'rgba(255,255,255,.82)', maxWidth: 380, margin: '0 0 30px', lineHeight: 1.55 }}>
          Slå in varorna, håll koll på lagret och följ intäkterna – matchdag efter matchdag.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 12.5, fontWeight: 800, letterSpacing: '.14em', color: 'rgba(255,255,255,.65)' }}>
          <span>GLÄDJE</span><span>·</span><span>GEMENSKAP</span><span>·</span><span>FOSTRAN</span><span>·</span><span>IDEALITET</span><span>·</span><span>KUNSKAP</span>
        </div>
      </div>

      <div className="login-form">
        <div style={{ width: 'min(380px,100%)' }}>
          {/* Bara på mobilen: den gröna panelen göms, så loggan står här i stället. */}
          <div className="login-mobile-brand">
            <img src={LOGO} alt="Sandareds IF" style={{ width: 96, height: 96, objectFit: 'contain' }} />
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 26, letterSpacing: '4px', color: 'var(--green-deep)', marginTop: 10 }}>KIOSK</div>
          </div>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 800, margin: '0 0 5px', color: 'var(--ink)' }}>Logga in</h2>
          <p style={{ color: 'var(--muted)', margin: '0 0 22px', fontSize: 14.5, fontWeight: 600 }}>Välj om du står i kiosken eller är admin.</p>
          {form}
          <div style={{ textAlign: 'center', marginTop: 22, fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Version {APP_VERSION}</div>
        </div>
      </div>
    </div>
  );
}

const S = {
  seg: { display: 'flex', background: 'var(--cream)', border: '1.5px solid var(--line)', borderRadius: 12, padding: 4, gap: 4, marginBottom: 20 },
  segBtn: { flex: 1, border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', background: 'transparent', color: 'var(--muted)', transition: 'all .12s' },
  segOn: { background: 'var(--green)', color: '#fff', boxShadow: '0 2px 0 var(--green-deep)' },
  lbl: { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 7 },
  inp: { width: '100%', padding: '13px 14px', border: '1.5px solid var(--line)', borderRadius: 11, fontSize: 16, background: '#fff', color: 'var(--ink)', outline: 'none' },
  err: { background: '#fbeae7', color: 'var(--bad)', borderRadius: 10, padding: '10px 13px', fontSize: 13.5, fontWeight: 700, marginTop: 12 },
  submit: { width: '100%', marginTop: 18, background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontWeight: 800, fontSize: 16, cursor: 'pointer', boxShadow: '0 3px 0 var(--green-deep)' },
  submitOff: { background: '#b9c7bd', boxShadow: '0 3px 0 #9fb0a4', cursor: 'not-allowed' },
};
